import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {authHeader} from '../../utils/headers';

const API_BASE = import.meta.env.VITE_APIURL || '';

export const modesOperatoiresApi = createApi({
    reducerPath: 'modesOperatoiresApi',
    baseQuery: fetchBaseQuery({
        baseUrl: API_BASE,
        prepareHeaders: (headers) => {
            const token = authHeader();
            if (typeof token === 'string' && token) {
                headers.set('authorization', token);
            }
            // Force le JSON-LD Hydra (sinon l'API peut répondre en JSON simple sans @id,
            // ce qui casse les sélecteurs basés sur l'IRI comme TypologieLavageSelector).
            headers.set('Accept', 'application/ld+json');
            return headers;
        },
    }),
    tagTypes: ['ModeOperatoire'],
    endpoints: (builder) => ({
        getModesOperatoires: builder.query({
            query: () => '/mode_operatoires',
            transformResponse: (r) => r?.['hydra:member'] ?? (Array.isArray(r) ? r : []),
            providesTags: (result) =>
                result
                    ? [...result.map((m) => ({type: 'ModeOperatoire', id: m.id})), {type: 'ModeOperatoire', id: 'LIST'}]
                    : [{type: 'ModeOperatoire', id: 'LIST'}],
        }),
        getModeOperatoire: builder.query({
            query: (id) => `/mode_operatoires/${id}`,
            providesTags: (result, error, id) => [{type: 'ModeOperatoire', id}],
        }),
        createModeOperatoire: builder.mutation({
            query: (body) => ({
                url: '/mode_operatoires',
                method: 'POST',
                headers: {'Content-Type': 'application/ld+json'},
                body: JSON.stringify(body),
            }),
            invalidatesTags: [{type: 'ModeOperatoire', id: 'LIST'}],
        }),
        updateModeOperatoire: builder.mutation({
            query: ({id, ...patch}) => ({
                url: `/mode_operatoires/${id}`,
                method: 'PATCH',
                headers: {'Content-Type': 'application/merge-patch+json'},
                body: JSON.stringify(patch),
            }),
            invalidatesTags: (result, error, {id}) => [{type: 'ModeOperatoire', id}, {type: 'ModeOperatoire', id: 'LIST'}],
        }),
        deleteModeOperatoire: builder.mutation({
            query: (id) => ({url: `/mode_operatoires/${id}`, method: 'DELETE'}),
            invalidatesTags: [{type: 'ModeOperatoire', id: 'LIST'}],
        }),

        // Fichiers (ressources autonomes rattachées à un mode op existant)
        createFichier: builder.mutation({
            query: ({modeOperatoireId, ...body}) => ({
                url: '/mode_operatoire_fichiers',
                method: 'POST',
                headers: {'Content-Type': 'application/ld+json'},
                body: JSON.stringify(body),
            }),
            invalidatesTags: (result, error, {modeOperatoireId}) => [{type: 'ModeOperatoire', id: modeOperatoireId}],
        }),
        deleteFichier: builder.mutation({
            query: ({id}) => ({url: `/mode_operatoire_fichiers/${id}`, method: 'DELETE'}),
            invalidatesTags: (result, error, {modeOperatoireId}) => [{type: 'ModeOperatoire', id: modeOperatoireId}],
        }),

        // Typologies (pour le sélecteur)
        getTypologies: builder.query({
            query: () => '/typologie_lavages',
            transformResponse: (r) => r?.['hydra:member'] ?? (Array.isArray(r) ? r : []),
        }),
    }),
});

export const {
    useGetModesOperatoiresQuery,
    useGetModeOperatoireQuery,
    useCreateModeOperatoireMutation,
    useUpdateModeOperatoireMutation,
    useDeleteModeOperatoireMutation,
    useCreateFichierMutation,
    useDeleteFichierMutation,
    useGetTypologiesQuery,
} = modesOperatoiresApi;
